<template>
  <div class="momentum-blueprint-viz">
    
    <div class="blueprint-header">
      <div class="info-block">
         <span class="label">实验室模拟</span>
         <span class="value">角动量定理实验柜</span>
      </div>
      <div class="formula-chip" v-latex>$L = r × p$</div>
    </div>

    <div class="viz-layout-horizontal">
      <div ref="canvasContainer" class="blueprint-stage">
          <!-- 独特的网格叠加层 -->
          <div class="blueprint-overlay"></div>
          
          <div class="hud-dashboard">
             <div class="dashboard-meter">
                <label>角速度 [ω]</label>
                <div class="meter-bar">
                   <div class="bar-fill" :style="{ width: Math.min(100, (omega * 10)) + '%' }"></div>
                </div>
                <span class="val">{{ omega.toFixed(2) }} rad/s</span>
             </div>
             <div class="dashboard-meter">
                <label>转动惯量 [I]</label>
                <div class="meter-bar">
                   <div class="bar-fill orange" :style="{ width: Math.min(100, (momentOfInertia * 5)) + '%' }"></div>
                </div>
                <span class="val">{{ momentOfInertia.toFixed(2) }} kg·m²</span>
             </div>
          </div>

          <!-- 矢量图例 -->
          <div class="vector-legend">
             <div class="legend-item"><i class="blue"></i> <span class="label">径向矢量 vec(r)</span></div>
             <div class="legend-item"><i class="green"></i> <span class="label">切向动量 vec(p)</span></div>
             <div class="legend-item"><i class="yellow"></i> <span class="label">角动量轴 vec(L)</span></div>
          </div>
      </div>

      <div class="blueprint-controls">
         <div class="control-card">
            <h4 class="title"><i class="el-icon-setting"></i> 系统参数调节</h4>
            <div class="s-group">
               <label>运行半径 (r)</label>
               <el-slider v-model="radius" :min="1" :max="4" :step="0.1" size="small"></el-slider>
               <span v-latex class="sub-label">影响转动惯量 $I = mr^2$</span>
            </div>
            <div class="s-group">
               <label>初始状态角动量 (L)</label>
               <el-slider v-model="L" :min="10" :max="100" size="small"></el-slider>
               <span class="sub-label">系统守恒总量</span>
            </div>
         </div>

         <div class="blueprint-note">
            <p><strong>物理详解：</strong> 在不受外力矩作用时，物体的角动量保持不变。当半径 $r$ 减小时，由于 $L = mr^2\omega$ 守恒，转动角速度 $\omega$ 必然会急剧增加。这正是芭蕾舞者通过收拢手臂实现快速自旋的物理原理。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default { props: { knowledgePoint: String },
  name: 'AngularMomentumViz',
  data() {
    return {
      radius: 2,
      angle: 0,
      L: 30, 
      running: true,
      time: 0,
      renderer: null,
      scene: null,
      camera: null,
      particle: null,
      rArrow: null,
      pArrow: null,
      lArrow: null,
      canvasContainer: null
    }
  },
  computed: {
    momentOfInertia() { return 1 * this.radius * this.radius; },
    omega() { return this.L / (this.momentOfInertia || 0.1); }
  },
  mounted() { 
    this.$nextTick(() => { this.tryInitThree(); });
  },
  beforeDestroy() { 
    this.running = false;
    if (this.initTimer) clearTimeout(this.initTimer);
    if (this.renderer) {
      if (this.canvasContainer && this.renderer.domElement) {
        if (this.canvasContainer.contains(this.renderer.domElement)) {
          this.canvasContainer.removeChild(this.renderer.domElement);
        }
      }
      this.renderer.dispose();
    }
  },
  methods: {
    tryInitThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      if (!this.canvasContainer) return;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;
      if (width === 0 || height === 0) {
        this.initTimer = setTimeout(this.tryInitThree, 200);
        return;
      }
      this.initThree();
    },
    initThree() {
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0xf1f5f9); // 纯净蓝图白背景

      this.camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
      this.camera.position.set(30, 50, 70);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      const grid = new THREE.GridHelper(80, 20, 0xcfd8dc, 0xe2e8f0);
      this.scene.add(grid);

      this.particle = new THREE.Mesh(
        new THREE.SphereGeometry(3, 32, 32),
        new THREE.MeshPhongMaterial({ color: 0x2196f3, shininess: 100 })
      );
      this.scene.add(this.particle);

      this.rArrow = new THREE.ArrowHelper(new THREE.Vector3(1,0,0), new THREE.Vector3(0,0,0), 10, 0x2196f3, 4, 1.5);
      this.pArrow = new THREE.ArrowHelper(new THREE.Vector3(0,0,1), new THREE.Vector3(10,0,0), 10, 0x4caf50, 3, 1.5);
      this.lArrow = new THREE.ArrowHelper(new THREE.Vector3(0,1,0), new THREE.Vector3(0,0,0), 20, 0xffc107, 5, 2);
      
      this.scene.add(this.rArrow);
      this.scene.add(this.pArrow);
      this.scene.add(this.lArrow);

      this.scene.add(new THREE.AmbientLight(0xffffff, 1));
      this.animate();
    },
    animate() {
      if (!this.running || !this.renderer) return;
      requestAnimationFrame(this.animate);
      
      this.time += 0.016 * this.omega * 0.5;
      const r_scale = this.radius * 6;
      
      const px = r_scale * Math.cos(this.time);
      const pz = r_scale * Math.sin(this.time);
      
      this.particle.position.set(px, 0, pz);
      
      this.rArrow.setDirection(new THREE.Vector3(px, 0, pz).normalize());
      this.rArrow.setLength(r_scale);
      
      this.pArrow.position.set(px, 0, pz);
      this.pArrow.setDirection(new THREE.Vector3(-Math.sin(this.time), 0, Math.cos(this.time)).normalize());
      this.pArrow.setLength(Math.max(5, this.omega * 5));
      
      this.lArrow.setLength(this.L * 0.5);

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.momentum-blueprint-viz {
  flex: 1; display: flex; flex-direction: column; height: 100%; min-height: 520px;
  background: #f8fafc; border: 1.5px solid #cbd5e1; border-radius: 12px; overflow: hidden;
  font-family: 'Inter', system-ui, sans-serif;
}

.blueprint-header {
  padding: 15px 25px; display: flex; justify-content: space-between; align-items: center;
  background: #ffffff; border-bottom: 2px solid #e2e8f0;
  .info-block {
     display: flex; flex-direction: column;
     .label { font-size: 0.7rem; color: #94a3b8; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; }
     .value { font-size: 1.1rem; color: #1e293b; font-weight: 800; }
  }
  .formula-chip {
     padding: 6px 16px; background: #f1f5f9; border-radius: 20px; font-weight: 700; color: #334155; font-family: monospace; border: 1px solid #e2e8f0;
  }
}

.viz-layout-horizontal { flex: 1; display: flex; padding: 15px; gap: 15px; min-height: 0; }

.blueprint-stage {
  flex: 1.6; min-height: 320px; border-radius: 12px; position: relative; overflow: hidden;
  border: 1px solid #cbd5e1; background: #fff; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);

  .blueprint-overlay {
    position: absolute; inset: 0; pointer-events: none;
    background-image: radial-gradient(#94a3b833 1px, transparent 1px); background-size: 20px 20px;
  }
  
  .hud-dashboard {
    position: absolute; top: 15px; left: 15px; width: 180px; z-index: 10;
    display: flex; flex-direction: column; gap: 10px; pointer-events: none;
    .dashboard-meter {
      background: rgba(255,255,255,0.9); border: 1px solid #e2e8f0; padding: 10px; border-radius: 8px;
      label { font-size: 10px; color: #64748b; font-weight: 700; display: block; margin-bottom: 4px; }
      .meter-bar { height: 4px; background: #f1f5f9; border-radius: 2px; margin-bottom: 4px; overflow: hidden;
         .bar-fill { height: 100%; background: #2196f3; transition: width 0.3s ease; }
         .bar-fill.orange { background: #f59e0b; }
      }
      .val { font-size: 14px; color: #1e293b; font-weight: 900; font-family: monospace; }
    }
  }

  .vector-legend {
    position: absolute; bottom: 15px; right: 15px; background: #fff; padding: 10px; border-radius: 8px; border: 1px solid #e2e8f0;
    display: flex; flex-direction: column; gap: 6px;
    .legend-item { display: flex; align-items: center; gap: 8px;
       i { width: 8px; height: 8px; border-radius: 50%; }
       i.blue { background: #2196f3; } i.green { background: #4caf50; } i.yellow { background: #ffc107; }
       .label { font-size: 9px; color: #475569; font-weight: bold; }
    }
  }
}

.blueprint-controls {
  flex: 1; display: flex; flex-direction: column; gap: 15px;
  .control-card {
    background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    .title { margin: 0 0 15px 0; font-size: 0.9rem; color: #1e293b; display: flex; align-items: center; gap: 8px; i { color: #64748b; } }
    .s-group { margin-bottom: 20px; label { display: block; font-size: 12px; color: #64748b; font-weight: 700; margin-bottom: 5px; } .sub-label { font-size: 10px; color: #94a3b8; font-style: italic; } }
  }
  .blueprint-note {
    background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 12px; padding: 15px;
    p { margin: 0; font-size: 12px; color: #475569; line-height: 1.6; }
    strong { color: #1e293b; }
  }
}

::v-deep .el-slider__runway { background-color: #f1f5f9; }
::v-deep .el-slider__bar { background-color: #1e293b; }
::v-deep .el-slider__button { border-color: #1e293b; }
</style>
